import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import { ChoiceChips } from "../design/ChoiceChips";
import { StatusPill } from "../design/StatusPill";
import type {
  PropertyLonaKind,
  PropertyOption,
  PropertyPromotion,
} from "../types";
import { toOpcionErrorMessage } from "./opcionRules";

/** Portals a listing can be published on; real account data never lives here. */
export const PROMOTION_PORTALS = [
  "Inmuebles24",
  "Vivanuncios",
  "Lamudi",
  "Propiedades.com",
  "Otra",
] as const;

const MIN_TAP_AREA = { minHeight: 44 } as const;

type SimpleKey = "photos_professional" | "video" | "virtual_tour";

const SIMPLE_ITEMS: readonly SimpleKey[] = [
  "photos_professional",
  "video",
  "virtual_tour",
];

/** The lona kind a freshly placed lona defaults to: the option's own operation. */
export const defaultLonaKind = (
  operation: PropertyOption["operation"],
): PropertyLonaKind => (operation === "renta" ? "se_renta" : "se_vende");

/** The patch to save when the lona checkbox changes (kind set exactly when placed). */
export const lonaPatch = (
  placed: boolean,
  operation: PropertyOption["operation"],
): Pick<PropertyPromotion, "lona_placed" | "lona_kind"> => ({
  lona_placed: placed,
  lona_kind: placed ? defaultLonaKind(operation) : null,
});

/** The patch to save for a portal selection (published exactly when some portal is picked). */
export const portalsPatch = (
  portals: readonly string[],
): Pick<PropertyPromotion, "published_portals" | "portals"> => ({
  published_portals: portals.length > 0,
  portals: [...portals],
});

type Props = {
  option: PropertyOption;
  /** Whoever may edit the option; everyone else gets the read-only view. */
  canEdit: boolean;
};

/**
 * Almikia Epic EC2d (TASK-138): the "Promoción" checklist of a property option. Each change
 * saves immediately with one `property_options` update carrying only promotion columns, so
 * status, price and responsable are never touched. Read-only viewers see every item as a
 * check or cross with text. BPO rows never reach this card (they are not options).
 */
export const PromotionCard = ({ option, canEdit }: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [values, setValues] = useState<PropertyPromotion>(() => ({
    lona_placed: option.lona_placed,
    lona_kind: option.lona_kind,
    photos_professional: option.photos_professional,
    video: option.video,
    virtual_tour: option.virtual_tour,
    published_portals: option.published_portals,
    portals: option.portals ?? [],
    published_social: option.published_social,
    ficha_ready: option.ficha_ready,
  }));
  // "Publicada en portales" ticked before any portal is chosen: nothing can be saved yet.
  const [isPortalDraft, setIsPortalDraft] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const t = (key: string) => translate(`crm.opciones.promotion.${key}`);

  const save = async (patch: Partial<PropertyPromotion>) => {
    setIsSaving(true);
    setIsSaved(false);
    try {
      await dataProvider.update("property_options", {
        id: option.id,
        data: patch,
        previousData: option,
      });
      setValues((current) => ({ ...current, ...patch }));
      setIsSaved(true);
    } catch (error) {
      notify(toOpcionErrorMessage(error), { type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const portalsChecked = values.published_portals || isPortalDraft;

  const togglePortals = (checked: boolean) => {
    if (checked) {
      setIsPortalDraft(true);
      return;
    }
    setIsPortalDraft(false);
    if (values.published_portals) save(portalsPatch([]));
  };

  const changePortals = (next: string[]) => {
    if (next.length === 0 && !values.published_portals) return;
    setIsPortalDraft(false);
    save(portalsPatch(next));
  };

  if (!canEdit) {
    return (
      <Card>
        <CardContent className="space-y-3" data-testid="promotion-card">
          <h3 className="font-semibold">{t("title")}</h3>
          <ul className="grid gap-2 sm:grid-cols-2">
            <ReadOnlyItem
              label={t("lona_placed")}
              isOn={values.lona_placed}
              detail={values.lona_kind ? t(values.lona_kind) : undefined}
            />
            {SIMPLE_ITEMS.map((key) => (
              <ReadOnlyItem key={key} label={t(key)} isOn={values[key]} />
            ))}
            <ReadOnlyItem
              label={t("published_portals")}
              isOn={values.published_portals}
              detail={
                values.published_portals ? values.portals.join(", ") : undefined
              }
            />
            <ReadOnlyItem
              label={t("published_social")}
              isOn={values.published_social}
            />
            <ReadOnlyItem label={t("ficha_ready")} isOn={values.ficha_ready} />
          </ul>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-3" data-testid="promotion-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold">{t("title")}</h3>
          {isSaved ? (
            <span role="status" className="text-sm text-muted-foreground">
              {t("saved")}
            </span>
          ) : null}
        </div>

        <CheckItem
          id="promotion-lona"
          label={t("lona_placed")}
          checked={values.lona_placed}
          disabled={isSaving}
          onChange={(checked) => save(lonaPatch(checked, option.operation))}
        />
        {values.lona_placed ? (
          <ChoiceChips
            label={t("lona_kind")}
            mode="single"
            options={[
              { id: "se_vende", label: t("se_vende") },
              { id: "se_renta", label: t("se_renta") },
            ]}
            value={values.lona_kind ? [values.lona_kind] : []}
            onChange={(next) => {
              const kind = next[0] as PropertyLonaKind | undefined;
              if (kind && kind !== values.lona_kind) save({ lona_kind: kind });
            }}
          />
        ) : null}

        {SIMPLE_ITEMS.map((key) => (
          <CheckItem
            key={key}
            id={`promotion-${key}`}
            label={t(key)}
            checked={values[key]}
            disabled={isSaving}
            onChange={(checked) =>
              save({ [key]: checked } as Pick<PropertyPromotion, SimpleKey>)
            }
          />
        ))}

        <CheckItem
          id="promotion-published_portals"
          label={t("published_portals")}
          checked={portalsChecked}
          disabled={isSaving}
          onChange={togglePortals}
        />
        {portalsChecked ? (
          <div className="space-y-2">
            <ChoiceChips
              label={t("portals")}
              mode="multi"
              options={PROMOTION_PORTALS.map((portal) => ({
                id: portal,
                label: portal,
              }))}
              value={values.portals}
              onChange={changePortals}
            />
            {values.portals.length === 0 ? (
              <p className="text-sm text-destructive">
                {t("portals_required")}
              </p>
            ) : null}
          </div>
        ) : null}

        <CheckItem
          id="promotion-published_social"
          label={t("published_social")}
          checked={values.published_social}
          disabled={isSaving}
          onChange={(checked) => save({ published_social: checked })}
        />
        <CheckItem
          id="promotion-ficha_ready"
          label={t("ficha_ready")}
          checked={values.ficha_ready}
          disabled={isSaving}
          onChange={(checked) => save({ ficha_ready: checked })}
        />
      </CardContent>
    </Card>
  );
};

const CheckItem = ({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <div className="flex min-h-11 items-center gap-3" style={MIN_TAP_AREA}>
    <Checkbox
      id={id}
      checked={checked}
      disabled={disabled}
      onCheckedChange={(next) => onChange(next === true)}
    />
    <Label htmlFor={id} className="min-h-11 flex-1 items-center text-base">
      {label}
    </Label>
  </div>
);

const ReadOnlyItem = ({
  label,
  isOn,
  detail,
}: {
  label: string;
  isOn: boolean;
  detail?: string;
}) => {
  const translate = useTranslate();
  const state = translate(
    isOn ? "crm.opciones.promotion.yes" : "crm.opciones.promotion.no",
  );
  return (
    <li className="flex flex-wrap items-center gap-2">
      <span className="text-base">{label}</span>
      <StatusPill
        tone={isOn ? "ok" : "not-ok"}
        text={detail ? `${state}: ${detail}` : state}
      />
    </li>
  );
};
